import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, MessageCircle, Star } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [popularRoutes, setPopularRoutes] = useState([]);

  useEffect(() => {
    const fetchPopularRoutes = async () => {
      try {
        const res = await fetch(`${API_BASE}/routes/popular`);
        if (res.ok) {
          const data = await res.json();
          setPopularRoutes(data.slice(0, 5));
        }
      } catch (err) {
        console.error('Failed to fetch popular routes:', err);
      }
    };
    fetchPopularRoutes();
  }, []);

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'All Routes', href: '/routes' },
    { label: 'Contact', href: '/#contact' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms', href: '/terms' },
  ];

  return (
    <footer className="bg-[#2d232e] pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center mb-6">
              <span className="text-2xl font-bold text-white">Kalamata Airport</span>
              <span className="text-2xl font-bold ml-2 text-[#36827f]">Taxi</span>
            </Link>
            <p className="text-white/60 mb-6 leading-relaxed">
              Premium airport transfer services in Messenia, Greece. Your comfort is our priority.
            </p>
            <div className="flex items-center gap-2 text-[#36827f]">
              <Star className="w-5 h-5 fill-current" />
              <Star className="w-5 h-5 fill-current" />
              <Star className="w-5 h-5 fill-current" />
              <Star className="w-5 h-5 fill-current" />
              <Star className="w-5 h-5 fill-current" />
              <span className="text-white/60 ml-2 text-sm">5.0 Rating</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-6 text-lg">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map(link => (
                <li key={link.label}>
                  <Link to={link.href} className="text-white/60 hover:text-[#36827f] transition">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Routes */}
          <div>
            <h4 className="text-white font-bold mb-6 text-lg">Popular Routes</h4>
            <ul className="space-y-3">
              {popularRoutes.map(route => (
                <li key={route.route_id}>
                  <Link 
                    to={`/routes/${route.from_slug}/${route.to_slug}`}
                    className="text-white/60 hover:text-[#36827f] transition flex items-center gap-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    {route.to_name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-6 text-lg">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:+306932421170" className="flex items-center gap-3 text-white/60 hover:text-[#36827f] transition">
                  <div className="w-10 h-10 rounded-lg bg-[#36827f]/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#36827f]" />
                  </div>
                  <span>+30 693 242 1170</span>
                </a>
              </li>
              <li>
                <a href="https://wa.me/306932421170" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/60 hover:text-[#25D366] transition">
                  <div className="w-10 h-10 rounded-lg bg-[#25D366]/20 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <span>WhatsApp</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@kalamataairporttaxi.com" className="flex items-center gap-3 text-white/60 hover:text-[#36827f] transition">
                  <div className="w-10 h-10 rounded-lg bg-[#36827f]/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#36827f]" />
                  </div>
                  <span>info@kalamataairporttaxi.com</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60">
                <div className="w-10 h-10 rounded-lg bg-[#36827f]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#36827f]" />
                </div>
                <span>Kalamata Airport, Greece</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} Kalamata Airport Taxi. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-white/40 hover:text-white/60 text-sm transition">Privacy Policy</Link>
            <Link to="/terms" className="text-white/40 hover:text-white/60 text-sm transition">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
